import {
  $applyNodeReplacement,
  DecoratorNode,
  DOMConversionMap,
  DOMConversionOutput,
  EditorConfig,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from "lexical";
import { ImageComponent } from "./imageUploadPlugin/ImageComponent";

function convertImageElement(domNode: Node): DOMConversionOutput | null {
  if (domNode instanceof HTMLImageElement) {
    const src = domNode.getAttribute("src");
    if (src) {
      const altText = domNode.getAttribute("alt") || "image";
      // Try to get width from style or attribute
      let width: number | undefined;
      const widthAttr = domNode.getAttribute("width");
      if (widthAttr) {
        const parsed = parseInt(widthAttr, 10);
        if (!isNaN(parsed) && parsed > 0) {
          width = parsed;
        }
      }
      const node = $createImageNode(src, altText, width);
      return { node };
    }
  }
  return null;
}

export type SerializedImageNode = Spread<
  {
    altText: string;
    src: string;
    width?: number;
    className?: string;
  },
  SerializedLexicalNode
>;

export class ImageNode extends DecoratorNode<JSX.Element> {
  __src: string;
  __altText: string;
  __width?: number;
  __className?: string;

  static getType(): string {
    return "image";
  }

  static clone(node: ImageNode): ImageNode {
    return new ImageNode(
      node.__src,
      node.__altText,
      node.__width,
      node.__className,
      node.__key
    );
  }

  static importDOM(): DOMConversionMap | null {
    return {
      img: () => ({
        conversion: convertImageElement,
        priority: 0,
      }),
    };
  }

  constructor(
    src: string,
    altText: string,
    width?: number,
    className?: string,
    key?: NodeKey
  ) {
    super(key);
    this.__src = src;
    this.__altText = altText;
    this.__width = width;
    this.__className = className;
  }

  createDOM(config: EditorConfig): HTMLElement {
    const span = document.createElement("span");
    const classNames = [config.theme.image, this.__className].filter(Boolean);
    if (classNames.length > 0) {
      span.className = classNames.join(" ");
    }
    return span;
  }

  updateDOM(prevNode: ImageNode): boolean {
    // Check if className changed
    if (this.__className !== prevNode.__className) {
      return true; // Re-create DOM if class changed
    }
    return false;
  }

  isInline(): boolean {
    return true;
  }

  getSrc(): string {
    return this.__src;
  }

  getAltText(): string {
    return this.__altText;
  }

  getWidth(): number | undefined {
    return this.__width;
  }

  setWidth(width: number): void {
    const writable = this.getWritable();
    writable.__width = width;
  }

  static importJSON(serializedNode: SerializedImageNode): ImageNode {
    const { src, altText, width, className } = serializedNode;
    return $createImageNode(src, altText, width, className);
  }

  exportJSON(): SerializedImageNode {
    return {
      altText: this.__altText,
      src: this.__src,
      type: "image",
      version: 1,
      width: this.__width,
      className: this.__className,
    };
  }

  decorate(): JSX.Element {
    return (
      <ImageComponent
        src={this.__src}
        altText={this.__altText}
        width={this.__width}
        nodeKey={this.__key}
        resizable={true}
      />
    );
  }
}

export function $createImageNode(
  src: string,
  altText: string,
  width?: number,
  className?: string
): ImageNode {
  return $applyNodeReplacement(new ImageNode(src, altText, width, className));
}

export function $isImageNode(
  node: LexicalNode | null | undefined
): node is ImageNode {
  return node instanceof ImageNode;
}
